import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  downloadRecording,
  fetchRecordingBlob,
  listCallRecordings,
  type CallRecording,
} from "../api/callRecordings";

/** Recorded warm transfers — the calls, not the campaign greetings.
 *
 * Each transfer has up to two parts: `consult` (the agent and the buyer alone,
 * before the customer is brought in) and `merged` (all three on the line).
 * Both exist because the two conference rooms share a bridge profile.
 */
/** Call length as a reviewer reads it: m:ss, because a transfer runs minutes
 *  rather than seconds. A dash when the file could not be read. */
function fmtLen(secs: number | null): string {
  if (secs == null) return "—";
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function CallRecordingsPage() {
  const [buyer, setBuyer] = useState("");
  const [minSeconds, setMinSeconds] = useState("");
  const [period, setPeriod] = useState<PeriodKey>("today");

  // The applied values, not the typed ones: a number still being typed ("18"
  // on the way to "180") must not refetch and show the wrong set.
  const [applied, setApplied] = useState<AppliedFilters>(() => ({
    ...periodRange("today"),
  }));

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "call-recordings",
      applied.buyer ?? "",
      applied.min_seconds ?? "",
      applied.date_from ?? "",
      applied.date_to ?? "",
    ],
    queryFn: () => listCallRecordings(applied),
  });

  function apply() {
    const n = parseInt(minSeconds, 10);
    setApplied({
      buyer: buyer || undefined,
      min_seconds: Number.isFinite(n) && n > 0 ? n : undefined,
      ...periodRange(period),
    });
  }

  const [playing, setPlaying] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);

  // Object URLs are a manual resource: one is revoked when another is played
  // and on unmount, or a long session quietly holds every recording it opened.
  useEffect(
    () => () => {
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    },
    [],
  );

  async function play(session: string, kind: "merged" | "consult") {
    const key = `${session}:${kind}`;
    setBusy(key);
    setFailed(null);
    try {
      const blob = await fetchRecordingBlob(session, kind);
      if (urlRef.current) URL.revokeObjectURL(urlRef.current);
      urlRef.current = URL.createObjectURL(blob);
      setPlaying(key);
    } catch {
      setFailed(key);
    } finally {
      setBusy(null);
    }
  }

  async function save(session: string, kind: "merged" | "consult") {
    const key = `${session}:${kind}`;
    setBusy(key);
    setFailed(null);
    try {
      await downloadRecording(session, kind);
    } catch {
      setFailed(key);
    } finally {
      setBusy(null);
    }
  }

  const items: CallRecording[] = data?.items ?? [];

  return (
    <div className="p-6">
      <div className="mb-4">
        <h1 className="text-xl font-semibold text-gray-900">Call Recordings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Recorded transfers &amp; callbacks — warm transfers, Press-1/Press-3
          transfers, and inbound DID callbacks. Times in{" "}
          {data?.timezone ?? "local time"}.
        </p>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-lg bg-white p-4 shadow">
        <div>
          <label htmlFor="buyer" className="block text-xs text-gray-500">
            Buyer
          </label>
          <select
            id="buyer"
            value={buyer}
            onChange={(e) => setBuyer(e.target.value)}
            className="mt-1 rounded border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">All buyers</option>
            {(data?.buyers ?? []).map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="minsecs" className="block text-xs text-gray-500">
            Call longer than (seconds)
          </label>
          <input
            id="minsecs"
            type="number"
            min={0}
            placeholder="e.g. 180"
            value={minSeconds}
            onChange={(e) => setMinSeconds(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && apply()}
            className="mt-1 w-40 rounded border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label htmlFor="period" className="block text-xs text-gray-500">
            Period
          </label>
          <select
            id="period"
            value={period}
            onChange={(e) => setPeriod(e.target.value as PeriodKey)}
            className="mt-1 rounded border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="week">This week</option>
            <option value="last7">Last 7 days</option>
            <option value="month">This month</option>
            <option value="all">All time</option>
          </select>
        </div>
        <button
          type="button"
          onClick={apply}
          className="rounded bg-brand px-3 py-2 text-sm text-white hover:bg-brand-dark"
        >
          Apply
        </button>
        {(applied.buyer || applied.min_seconds) && (
          <button
            type="button"
            onClick={() => {
              setBuyer("");
              setMinSeconds("");
              setPeriod("today");
              setApplied({ ...periodRange("today") });
            }}
            className="rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Clear
          </button>
        )}
        {(applied.buyer || applied.min_seconds) && (
          <span className="text-xs text-gray-500">
            Showing {data?.items.length ?? 0}
            {applied.buyer ? ` for ${applied.buyer}` : ""}
            {applied.min_seconds ? ` over ${applied.min_seconds}s` : ""}
          </span>
        )}
      </div>

      {/* The answer to the question the page is opened to settle: how many
          calls match this buyer, over this many seconds, in this period. */}
      {data && (
        <div className="mb-4 rounded-lg bg-white p-4 shadow">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="text-3xl font-semibold tabular-nums text-gray-900">
              {data.matching_count}
            </span>
            <span className="text-sm text-gray-600">
              {data.matching_count === 1 ? "call" : "calls"} over{" "}
              <strong>{data.long_call_seconds}s</strong>
              {data.filtered_by.buyer ? (
                <>
                  {" "}to <strong>{data.filtered_by.buyer}</strong>
                </>
              ) : (
                " across all buyers"
              )}
              {" — "}
              {describeRange(data.filtered_by.date_from, data.filtered_by.date_to)}
            </span>
          </div>
        </div>
      )}

      {(data?.by_campaign?.length ?? 0) > 0 && (
        <div className="mb-4 overflow-hidden rounded-lg bg-white shadow">
          <div className="border-b border-gray-100 px-4 py-2 text-sm font-medium text-gray-900">
            {describeRange(data?.filtered_by.date_from ?? null,
                           data?.filtered_by.date_to ?? null)}{" "}
            — recordings over {data?.long_call_seconds ?? 180}s, by campaign
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">Campaign</th>
                <th className="px-4 py-2 text-right">Recorded today</th>
                <th className="px-4 py-2 text-right">
                  Over {data?.long_call_seconds ?? 180}s
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data?.by_campaign.map((c) => (
                <tr key={c.campaign_id ?? "none"}>
                  <td className="px-4 py-2">{c.campaign_name ?? "—"}</td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {c.recordings}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    <span
                      className={
                        c.over_180s > 0
                          ? "rounded bg-green-100 px-2 py-0.5 text-green-800"
                          : "text-gray-400"
                      }
                    >
                      {c.over_180s}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isLoading && <div className="text-sm text-gray-500">Loading…</div>}
      {error && (
        <div className="rounded bg-red-50 p-3 text-sm text-red-700">
          Couldn’t load recordings.
        </div>
      )}

      {!isLoading && !error && items.length === 0 && (
        <div className="rounded-lg bg-white p-6 text-sm text-gray-600 shadow">
          <p className="font-medium text-gray-900">No recordings yet.</p>
          <p className="mt-1">
            Transfers are recorded from 21 September. Calls transferred before
            then have no audio, and one appears here as soon as an agent
            completes a transfer.
          </p>
        </div>
      )}

      {items.length > 0 && (
        <div className="overflow-hidden rounded-lg bg-white shadow">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-2">When</th>
                <th className="px-4 py-2">Agent</th>
                <th className="px-4 py-2">Buyer</th>
                <th className="px-4 py-2">Outcome</th>
                <th className="px-4 py-2">Recording</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((r) => (
                <tr key={r.session_id}>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {r.started_local ?? "—"}
                  </td>
                  <td className="px-4 py-2">{r.agent ?? "—"}</td>
                  <td className="px-4 py-2">{r.buyer ?? "—"}</td>
                  <td className="px-4 py-2">
                    <span
                      className={
                        "rounded px-2 py-0.5 text-xs " +
                        (r.merged
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-200 text-gray-600")
                      }
                    >
                      {r.merged
                        ? r.outcome === "Connected"
                          ? "Connected"
                          : "Merged"
                        : (r.outcome ?? "Not merged")}
                    </span>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap items-center gap-2">
                      {r.parts.map((p) => {
                        const key = `${r.session_id}:${p.kind}`;
                        const isDirect =
                          r.session_id.startsWith("f1") ||
                          r.session_id.startsWith("f2");
                        return (
                          <span key={p.kind} className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => play(r.session_id, p.kind)}
                              disabled={busy === key}
                              className="rounded bg-brand px-2 py-1 text-xs text-white hover:bg-brand-dark disabled:opacity-50"
                            >
                              {busy === key
                                ? "Loading…"
                                : p.kind === "merged"
                                  ? isDirect
                                    ? "Play recording"
                                    : "Play three-way"
                                  : "Play consult"}
                            </button>
                            <button
                              type="button"
                              onClick={() => save(r.session_id, p.kind)}
                              disabled={busy === key}
                              className="rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              Download
                            </button>
                            <span className="text-xs text-gray-500 tabular-nums">
                              {fmtLen(p.duration_seconds)}
                            </span>
                            {failed === key && (
                              <span className="text-xs text-red-600">
                                Couldn’t load that file.
                              </span>
                            )}
                          </span>
                        );
                      })}
                      {playing?.startsWith(r.session_id) && urlRef.current && (
                        <audio
                          controls
                          autoPlay
                          src={urlRef.current}
                          className="h-8"
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


type PeriodKey = "today" | "yesterday" | "week" | "last7" | "month" | "all";

interface AppliedFilters {
  buyer?: string;
  min_seconds?: number;
  date_from?: string;
  date_to?: string;
}

/** A named period as the inclusive local dates the API filters on.
 *
 * Local dates, not UTC: a floor manager asking for "today" means their day,
 * and a call at 6pm Central belongs to today even though it is already
 * tomorrow in UTC. The server applies the account timezone to these.
 *
 * "This week" starts Monday — the working week these floors run, not the
 * Sunday that `getDay()` would give you by default.
 */
function periodRange(p: PeriodKey): { date_from?: string; date_to?: string } {
  if (p === "all") return {};
  const now = new Date();
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate(),
    ).padStart(2, "0")}`;
  const start = new Date(now);
  const end = new Date(now);
  if (p === "yesterday") {
    start.setDate(start.getDate() - 1);
    end.setDate(end.getDate() - 1);
  } else if (p === "week") {
    // Monday of the current week. getDay() is 0 for Sunday, so Sunday must
    // look back six days rather than forward one.
    const dow = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - dow);
  } else if (p === "last7") {
    start.setDate(start.getDate() - 6);
  } else if (p === "month") {
    start.setDate(1);
  }
  return { date_from: iso(start), date_to: iso(end) };
}

/** A date range as a manager would say it, not as ISO strings. */
function describeRange(from: string | null, to: string | null): string {
  if (!from && !to) return "all time";
  if (from && to && from === to) {
    const today = new Date();
    const iso = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    return from === iso ? `today (${from})` : from;
  }
  if (from && to) return `${from} to ${to}`;
  return from ? `since ${from}` : `up to ${to}`;
}
